import { useState } from 'react'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { printEventLog } from '@/utils/printUtils'
import { RECURRENCE_OPTIONS, generateRecurringDates, todayYMD, nowTimeStr } from '@/utils/dateUtils'

// Pencil icon used for the inline edit button on each event card
function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H7v-3.414a2 2 0 01.586-1.414z" />
    </svg>
  )
}

const EVENT_TYPES = [
  { value: 'exchange',   label: '🔄 Custody Exchange',  color: 'bg-blue-100 text-blue-700' },
  { value: 'violation',  label: '🚫 Custody Violation',  color: 'bg-red-100 text-red-700' },
  { value: 'missed',     label: '❌ Missed Visit',       color: 'bg-orange-100 text-orange-700' },
  { value: 'incident',   label: '⚠️ Notable Incident',   color: 'bg-yellow-100 text-yellow-700' },
  { value: 'medical',    label: '🏥 Medical Event',      color: 'bg-purple-100 text-purple-700' },
  { value: 'school',     label: '🎓 School Event',       color: 'bg-green-100 text-green-700' },
  { value: 'other',      label: '📌 Other',              color: 'bg-slate-100 text-slate-600' },
]

const EMPTY_FORM = {
  date: '', time: '', endTime: '', type: '', location: '', description: '',
  witnesses: '', evidence: false, childPresent: true,
  recurring: false, recurrence: 'weekly', recurrenceEnd: '',
}

function freshForm() { return { ...EMPTY_FORM, date: todayYMD(), time: nowTimeStr() } }

export default function EventLog() {
  const { user, events, addEvent, updateEvent } = useApp()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(freshForm)
  const [formErrors, setFormErrors] = useState({})
  const [filterType, setFilterType] = useState('all')

  // Edit state
  const [editingEvent, setEditingEvent] = useState(null)   // event object being edited, or null
  const [editForm, setEditForm] = useState({})
  const [editSaving, setEditSaving] = useState(false)

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFormErrors(prev => ({ ...prev, [k]: '' })) }
  const setEdit = (k, v) => setEditForm(p => ({ ...p, [k]: v }))

  const openEdit = (event) => {
    setEditingEvent(event)
    setEditForm({
      date:        event.date        || '',
      time:        event.time        || '',
      type:        event.type        || '',
      location:    event.location    || '',
      description: event.description || '',
      witnesses:   event.witnesses   || '',
      evidence:    !!event.evidence,
      childPresent: event.childPresent !== false,
    })
  }

  const closeEdit = () => { setEditingEvent(null); setEditForm({}) }

  const saveEdit = async () => {
    if (!editForm.date || !editForm.type || !editForm.description) return
    setEditSaving(true)
    try {
      await updateEvent(editingEvent.id, editForm)
      closeEdit()
    } finally {
      setEditSaving(false)
    }
  }

  const save = async () => {
    const errors = {}
    if (!form.type) errors.type = 'Event type is required'
    if (!form.description.trim()) errors.description = 'Description is required'
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return }
    setFormErrors({})
    if (form.recurring && form.recurrenceEnd) {
      const dates = generateRecurringDates(form.date, form.recurrence, form.recurrenceEnd)
      const { recurring, recurrence, recurrenceEnd, ...base } = form
      const recurrenceGroup = form.date + '-' + form.recurrence
      await Promise.all(dates.map(date => addEvent({ ...base, date, recurrenceGroup })))
    } else {
      await addEvent({ ...form })
    }
    setForm(freshForm())
    setShowForm(false)
  }

  const filtered = filterType === 'all' ? events : events.filter(e => e.type === filterType)
  const eventTag = (type) => EVENT_TYPES.find(t => t.value === type)

  const stats = {
    total:     events.length,
    violations: events.filter(e => e.type === 'violation').length,
    missed:    events.filter(e => e.type === 'missed').length,
  }

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Custody Event Log" showBack
        right={
          <div className="flex items-center gap-2">
            {events.length > 0 && (
              <button
                onClick={() => printEventLog({ userName: user?.name, state: user?.state, events })}
                className="tap-target w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80 hover:bg-white/30"
                title="Export as PDF"
                aria-label="Export event log as PDF">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
              </button>
            )}
            <button onClick={() => setShowForm(true)}
              className="tap-target w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80 hover:bg-white/30"
              aria-label="Add event entry">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        }
      />

      <div className="screen no-scrollbar px-4 py-4 flex flex-col gap-4">

        {/* Stats row */}
        {events.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Total Events', value: stats.total, color: 'text-brand-700' },
              { label: 'Violations', value: stats.violations, color: 'text-red-600' },
              { label: 'Missed Visits', value: stats.missed, color: 'text-orange-600' },
            ].map(s => (
              <div key={s.label} className="card text-center py-3">
                <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {events.length === 0 && !showForm && (
          <div className="card bg-brand-50 border border-brand-200 text-center py-8 flex flex-col items-center gap-3">
            <span className="text-4xl">📋</span>
            <div>
              <p className="font-bold text-slate-800">Custody Event Log</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Log custody exchanges, violations, missed visits, and incidents. Every entry is timestamped and formatted for potential court use.
              </p>
            </div>
            <Button variant="primary" size="md" onClick={() => setShowForm(true)}>
              Log First Event
            </Button>
          </div>
        )}

        {/* Add form */}
        {showForm && (
          <div className="card border border-brand-200 flex flex-col gap-4 animate-slide-up">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-800">New Event</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-500 tap-target" aria-label="Close form">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input label="Date" type="date" value={form.date} onChange={e => set('date', e.target.value)} required />
              <div className="flex flex-col gap-1">
                <Input label="Start Time" type="time" value={form.time} onChange={e => set('time', e.target.value)} />
                <Input label="End Time" type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)} />
              </div>
            </div>

            {/* Recurring toggle */}
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.recurring}
                  onChange={e => set('recurring', e.target.checked)}
                  className="w-4 h-4 rounded accent-brand-600" />
                <span className="text-sm font-semibold text-slate-700">Recurring schedule</span>
              </label>

              {form.recurring && (
                <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 flex flex-col gap-3">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-2">Frequency</label>
                    <div className="grid grid-cols-4 gap-1.5">
                      {RECURRENCE_OPTIONS.map(opt => (
                        <button key={opt.value} type="button"
                          onClick={() => set('recurrence', opt.value)}
                          className={`text-xs px-2 py-2 rounded-lg font-medium border transition-all ${
                            form.recurrence === opt.value
                              ? 'bg-brand-700 text-white border-brand-700'
                              : 'bg-white text-slate-600 border-slate-200'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Input label="Repeat until" type="date" value={form.recurrenceEnd}
                    onChange={e => set('recurrenceEnd', e.target.value)}
                    min={form.date || undefined} required />
                  {form.date && form.recurrenceEnd && (
                    <p className="text-xs text-brand-700 font-medium">
                      {generateRecurringDates(form.date, form.recurrence, form.recurrenceEnd).length} occurrences will be created
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-2">Event type <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 gap-2">
                {EVENT_TYPES.map(et => (
                  <button key={et.value} onClick={() => set('type', et.value)}
                    className={`text-xs px-2 py-2 rounded-xl font-medium border-2 text-left transition-all ${
                      form.type === et.value ? et.color + ' border-current' : 'bg-white text-slate-700 border-slate-300'}`}>
                    {et.label}
                  </button>
                ))}
              </div>
              {formErrors.type && <p className="text-red-500 text-sm mt-1">{formErrors.type}</p>}
            </div>

            <Input label="Location" type="text" value={form.location}
              onChange={e => set('location', e.target.value)}
              placeholder="e.g. 123 Main St, or school parking lot" />

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-2">Description <span className="text-red-500">*</span></label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Factual description of exactly what happened. Be specific: times, words spoken, actions taken."
                rows={3} className="input-base resize-none text-sm" />
              {formErrors.description && <p className="text-red-500 text-sm mt-1">{formErrors.description}</p>}
            </div>

            <Input label="Witnesses (optional)" type="text" value={form.witnesses}
              onChange={e => set('witnesses', e.target.value)}
              placeholder="Names of anyone who witnessed this event" />

            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.evidence}
                  onChange={e => set('evidence', e.target.checked)}
                  className="w-4 h-4 rounded accent-brand-600" />
                <span className="text-sm text-slate-700">Evidence saved (photos/texts)</span>
              </label>
            </div>

            <div className="flex gap-3">
              <Button variant="ghost" size="md" className="flex-1" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button variant="primary" size="md" className="flex-1"
                disabled={form.recurring && !form.recurrenceEnd} onClick={save}>
                {form.recurring && form.date && form.recurrenceEnd
                  ? `Save ${generateRecurringDates(form.date, form.recurrence, form.recurrenceEnd).length} Events`
                  : 'Save Event'}
              </Button>
            </div>
          </div>
        )}

        {/* Filter chips */}
        {events.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <button onClick={() => setFilterType('all')}
              className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-medium transition-all ${filterType === 'all' ? 'bg-brand-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
              All ({events.length})
            </button>
            {EVENT_TYPES.filter(et => events.some(e => e.type === et.value)).map(et => (
              <button key={et.value} onClick={() => setFilterType(et.value)}
                className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-medium transition-all ${filterType === et.value ? 'bg-brand-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {et.label} ({events.filter(e => e.type === et.value).length})
              </button>
            ))}
          </div>
        )}

        {/* Event list */}
        {filtered.map(event => {
          const tag = eventTag(event.type)
          return (
            <div key={event.id} className="card flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-500">
                    {event.date}
                    {event.time ? ` at ${event.time}${event.endTime ? ` – ${event.endTime}` : ''}` : ''}
                  </p>
                  {event.location && <p className="text-sm text-slate-500 mt-0.5">📍 {event.location}</p>}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tag?.color}`}>
                    {tag?.label}
                  </span>
                  <button
                    onClick={() => openEdit(event)}
                    className="tap-target w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-brand-100 hover:text-brand-700 transition-colors"
                    aria-label="Edit event">
                    <PencilIcon />
                  </button>
                </div>
              </div>
              <p className="text-base text-slate-700 leading-relaxed">{event.description}</p>
              <div className="flex flex-wrap gap-2">
                {event.witnesses && <span className="text-sm text-slate-500">👥 {event.witnesses}</span>}
                {event.evidence && <span className="text-sm text-green-600 font-medium">📎 Evidence saved</span>}
              </div>
            </div>
          )
        })}

        {/* Edit modal */}
        {editingEvent && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={closeEdit}>
            <div
              className="w-full max-w-lg bg-white rounded-t-2xl shadow-xl flex flex-col gap-4 p-5 animate-slide-up max-h-[90vh] overflow-y-auto no-scrollbar"
              onClick={e => e.stopPropagation()}>

              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-800">Edit Event</h3>
                <button onClick={closeEdit} className="text-slate-500 tap-target" aria-label="Close edit form">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input label="Date" type="date" value={editForm.date}
                  onChange={e => setEdit('date', e.target.value)} required />
                <Input label="Time" type="time" value={editForm.time}
                  onChange={e => setEdit('time', e.target.value)} />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">
                  Event type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {EVENT_TYPES.map(et => (
                    <button key={et.value} onClick={() => setEdit('type', et.value)}
                      className={`text-xs px-2 py-2 rounded-xl font-medium border-2 text-left transition-all ${
                        editForm.type === et.value ? et.color + ' border-current' : 'bg-white text-slate-700 border-slate-300'}`}>
                      {et.label}
                    </button>
                  ))}
                </div>
              </div>

              <Input label="Location" type="text" value={editForm.location}
                onChange={e => setEdit('location', e.target.value)}
                placeholder="e.g. 123 Main St, or school parking lot" />

              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea value={editForm.description} onChange={e => setEdit('description', e.target.value)}
                  placeholder="Factual description of exactly what happened."
                  rows={3} className="input-base resize-none text-sm" />
              </div>

              <Input label="Witnesses (optional)" type="text" value={editForm.witnesses}
                onChange={e => setEdit('witnesses', e.target.value)}
                placeholder="Names of anyone who witnessed this event" />

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editForm.evidence}
                    onChange={e => setEdit('evidence', e.target.checked)}
                    className="w-4 h-4 rounded accent-brand-600" />
                  <span className="text-sm text-slate-700">Evidence saved (photos/texts)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editForm.childPresent}
                    onChange={e => setEdit('childPresent', e.target.checked)}
                    className="w-4 h-4 rounded accent-brand-600" />
                  <span className="text-sm text-slate-700">Child present</span>
                </label>
              </div>

              <div className="flex gap-3">
                <Button variant="ghost" size="md" className="flex-1" onClick={closeEdit}>Cancel</Button>
                <Button variant="primary" size="md" className="flex-1"
                  disabled={!editForm.date || !editForm.type || !editForm.description || editSaving}
                  onClick={saveEdit}>
                  {editSaving ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {events.length > 0 && (
          <>
            <div className="flex gap-2">
              <Button variant="secondary" size="md" className="flex-1" onClick={() => setShowForm(true)}>
                + Log Event
              </Button>
              <Button variant="ghost" size="md" className="flex-1"
                onClick={() => printEventLog({ userName: user?.name, state: user?.state, events })}>
                🖨️ Export PDF
              </Button>
            </div>
            <div className="disclaimer">
              ⚠️ This log is for your personal records. Share it with your attorney. Courts expect factual, unemotional entries — let the facts speak for themselves.
            </div>
          </>
        )}
      </div>
    </div>
  )
}
